import { useEffect, useState } from 'react';
import { supabase, isPlaceholderSupabase } from '../supabase';

export interface ActiveOrg {
  id: string;
  name: string;
  logo_url: string | null;
  primary_color: string | null;
  secondary_color: string | null;
  country: string | null;
}

interface Result {
  org: ActiveOrg | null;
  loading: boolean;
  errorMessage?: string;
  refresh: () => void;
}

/**
 * Resolves the active team's org row. Two-step: read the team's org_id
 * via teams.org_id → fetch the organizations row. Returns null when
 * the team has no org (legacy demo teams) or in offline mode.
 */
export function useActiveOrg(teamId: string | null | undefined): Result {
  const [org, setOrg] = useState<ActiveOrg | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    if (!teamId) {
      setOrg(null);
      return;
    }
    if (isPlaceholderSupabase()) {
      setOrg(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setErrorMessage(undefined);
    void (async () => {
      const { data: team, error: teamErr } = await supabase
        .from('teams')
        .select('org_id')
        .eq('id', teamId)
        .maybeSingle();
      if (cancelled) return;
      if (teamErr || !team?.org_id) {
        setOrg(null);
        setLoading(false);
        if (teamErr) setErrorMessage(teamErr.message);
        return;
      }
      const { data: orgRow, error: orgErr } = await supabase
        .from('organizations')
        .select('id, name, logo_url, primary_color, secondary_color, country')
        .eq('id', team.org_id)
        .maybeSingle();
      if (cancelled) return;
      if (orgErr) {
        setErrorMessage(orgErr.message);
        setLoading(false);
        return;
      }
      setOrg((orgRow as ActiveOrg) ?? null);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [teamId, refreshTick]);

  return { org, loading, errorMessage, refresh: () => setRefreshTick((n) => n + 1) };
}
